// src/ui/screens/settings/OptimizeAppScreen.jsx
//
// Web port of lib/ui/screens/settings/optimize_app_page.dart.
//
// The Flutter version reads the device's temp/documents directories directly.
// A browser has no filesystem access, so this uses the closest web
// equivalents:
//   - navigator.storage.estimate()  → total usage / quota (StorageManager API)
//   - caches.keys() / caches.delete() → the Cache Storage API, which is the
//     web's analogue of "temporary files, thumbnails, previews"
//   - the remainder of `estimate().usage` (IndexedDB, localStorage, etc.) is
//     shown as "App Data", mirroring the Documents-dir bucket in the app.

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

const COLORS = {
  orange: '#FF8C08',
  bg: '#08090B',
  card: '#23262C',
  cardLight: '#2C2F36',
};

const formatBytes = (bytes) => {
  if (!bytes || bytes <= 0) return '0 KB';
  const units = ['B', 'KB', 'MB', 'GB'];
  let size = bytes;
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }
  const decimals = size < 10 && unitIndex > 0 ? 1 : 0;
  return `${size.toFixed(decimals)} ${units[unitIndex]}`;
};

const UsageRow = ({ label, sublabel, icon, bytes, totalBytes, loading }) => {
  const fraction = totalBytes > 0 ? Math.min(bytes / totalBytes, 1) : 0;

  return (
    <div className="p-3.5 rounded-2xl" style={{ backgroundColor: COLORS.cardLight }}>
      <div className="flex items-center gap-3">
        <div
          className="w-9 h-9 rounded-[10px] flex items-center justify-center shrink-0"
          style={{ backgroundColor: `${COLORS.orange}1F` }}
        >
          {icon}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-white text-sm font-semibold">{label}</p>
          <p className="text-white/40 text-[11px]">{sublabel}</p>
        </div>
        {!loading && (
          <span className="text-white text-[13px] font-bold shrink-0">{formatBytes(bytes)}</span>
        )}
      </div>
      <div className="mt-2.5 h-1.5 rounded-full bg-white/10 overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-300"
          style={{
            width: loading ? '30%' : `${fraction * 100}%`,
            backgroundColor: COLORS.orange,
            opacity: loading ? 0.4 : 1,
          }}
        />
      </div>
    </div>
  );
};

export const OptimizeAppScreen = () => {
  const navigate = useNavigate();

  const [isLoadingUsage, setIsLoadingUsage] = useState(true);
  const [isClearing, setIsClearing] = useState(false);
  const [toast, setToast] = useState(null);

  const [cacheBytes, setCacheBytes] = useState(0);
  const [appDataBytes, setAppDataBytes] = useState(0);
  const [totalBytes, setTotalBytes] = useState(0);
  const [quotaSupported, setQuotaSupported] = useState(true);

  const showToast = useCallback((message, isError = false) => {
    setToast({ message, isError });
    window.setTimeout(() => setToast(null), 2500);
  }, []);

  // ── Storage calculation ──────────────────────────────────────────────

  const loadStorageUsage = useCallback(async () => {
    setIsLoadingUsage(true);
    try {
      if (!(navigator.storage && navigator.storage.estimate)) {
        setQuotaSupported(false);
        setIsLoadingUsage(false);
        return;
      }

      const estimate = await navigator.storage.estimate();
      const usage = estimate.usage || 0;

      // Chromium exposes a breakdown via usageDetails; other browsers don't.
      const details = estimate.usageDetails || {};
      const cacheUsage = details.caches ?? 0;

      // Fall back: treat everything else as "App Data" (IndexedDB,
      // localStorage, serviceWorkerRegistrations, etc).
      const appData = Math.max(usage - cacheUsage, 0);

      setCacheBytes(cacheUsage);
      setAppDataBytes(appData);
      setTotalBytes(usage);
      setQuotaSupported(true);
    } catch (error) {
      console.error('loadStorageUsage error:', error);
      showToast('Could not read storage usage', true);
    } finally {
      setIsLoadingUsage(false);
    }
  }, [showToast]);

  useEffect(() => {
    loadStorageUsage();
  }, [loadStorageUsage]);

  // ── Clear cache ───────────────────────────────────────────────────────

  const clearCache = async () => {
    if (isClearing) return;
    setIsClearing(true);
    try {
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((key) => caches.delete(key)));
      }
      await loadStorageUsage();
      showToast('Cache cleared successfully');
    } catch (error) {
      console.error('clearCache error:', error);
      showToast('Failed to clear cache. Please try again.', true);
    } finally {
      setIsClearing(false);
    }
  };

  const cacheAlreadyEmpty = cacheBytes === 0 && !isLoadingUsage;
  const clearDisabled = isClearing || isLoadingUsage || cacheAlreadyEmpty || !quotaSupported;

  const StorageIcon = () => (
    <svg className="w-[30px] h-[30px]" fill="none" stroke={COLORS.orange} viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 01-2-2V5a2 2 0 012-2h14a2 2 0 012 2v1a2 2 0 01-2 2M5 8v11a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
    </svg>
  );

  const CacheIcon = () => (
    <svg className="w-[18px] h-[18px]" fill="none" stroke={COLORS.orange} viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582M20 20v-5h-.581M5.5 9A7.5 7.5 0 0119 9M18.5 15a7.5 7.5 0 01-13.5 0" />
    </svg>
  );

  const AppDataIcon = () => (
    <svg className="w-[18px] h-[18px]" fill="none" stroke={COLORS.orange} viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V7z" />
    </svg>
  );

  const ClearButtonContent = () =>
    isClearing ? (
      <div className="w-6 h-6 rounded-full border-2 border-black/30 border-t-black animate-spin" />
    ) : (
      <>
        <svg className="w-5 h-5" fill="none" stroke="black" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3M4 7h16" />
        </svg>
        <span>{cacheAlreadyEmpty ? 'Cache Already Empty' : 'Clear Cache'}</span>
      </>
    );

  // ── Render ────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen" style={{ backgroundColor: COLORS.bg }}>
      {/* ══════════════════════════════════════════════════════════════════
          MOBILE LAYOUT (unchanged) — visible below the md breakpoint
         ══════════════════════════════════════════════════════════════════ */}
      <div className="md:hidden">
        {/* Top bar */}
        <div className="flex items-center gap-3 px-3 pt-2 pb-1">
          <button
            onClick={() => navigate(-1)}
            className="w-10 h-10 rounded-full flex items-center justify-center border border-white/10"
            style={{ backgroundColor: COLORS.card }}
          >
            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <span className="text-white text-xl font-bold">Optimize App</span>
        </div>

        <div className="px-4 pt-2 pb-8">
          {/* Usage card */}
          <div className="w-full p-6 rounded-[20px] flex flex-col items-center" style={{ backgroundColor: COLORS.card }}>
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center"
              style={{ backgroundColor: `${COLORS.orange}1F` }}
            >
              <StorageIcon />
            </div>
            <p className="text-white/50 text-[13px] mt-4">Total App Storage</p>
            <div className="mt-1.5 h-[38px] flex items-center">
              {isLoadingUsage ? (
                <div
                  className="w-[30px] h-[30px] rounded-full border-2 border-t-transparent animate-spin"
                  style={{ borderColor: COLORS.orange, borderTopColor: 'transparent' }}
                />
              ) : quotaSupported ? (
                <span className="text-white text-[28px] font-extrabold">{formatBytes(totalBytes)}</span>
              ) : (
                <span className="text-white/40 text-sm">Not available in this browser</span>
              )}
            </div>
          </div>

          {quotaSupported && (
            <>
              {/* Breakdown card */}
              <div className="mt-5 p-4 rounded-[20px]" style={{ backgroundColor: COLORS.card }}>
                <p className="text-white text-[15px] font-bold mb-4">Storage Breakdown</p>
                <div className="space-y-3.5">
                  <UsageRow
                    label="Cache"
                    sublabel="Cached files, thumbnails, previews"
                    icon={<CacheIcon />}
                    bytes={cacheBytes}
                    totalBytes={totalBytes}
                    loading={isLoadingUsage}
                  />
                  <UsageRow
                    label="App Data"
                    sublabel="Saved data & local settings"
                    icon={<AppDataIcon />}
                    bytes={appDataBytes}
                    totalBytes={totalBytes}
                    loading={isLoadingUsage}
                  />
                </div>
              </div>

              {/* Clear cache button */}
              <button
                onClick={clearCache}
                disabled={clearDisabled}
                className="w-full mt-6 h-[54px] rounded-2xl flex items-center justify-center gap-2.5 font-bold text-black text-base disabled:opacity-30"
                style={{ backgroundColor: COLORS.orange }}
              >
                <ClearButtonContent />
              </button>
            </>
          )}

          <p className="mt-3 px-2 text-center text-white/20 text-xs leading-relaxed">
            Clearing cache removes cached files like image previews. Your account,
            messages, and saved data stay untouched.
          </p>
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════════
          DESKTOP LAYOUT (new) — visible at md breakpoint and up.
          Sticky header, then a two-column dashboard: a compact storage
          summary card on the left, breakdown + action on the right.
         ══════════════════════════════════════════════════════════════════ */}
      <div className="hidden md:block">
        <div className="sticky top-0 z-10 backdrop-blur-sm border-b border-white/10" style={{ backgroundColor: `${COLORS.bg}E6` }}>
          <div className="max-w-5xl mx-auto px-8 h-20 flex items-center gap-5">
            <button
              onClick={() => navigate(-1)}
              className="w-10 h-10 rounded-full flex items-center justify-center border border-white/10 hover:bg-white/10 transition-colors"
              style={{ backgroundColor: COLORS.card }}
              aria-label="Go back"
            >
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div>
              <h1 className="text-white text-2xl font-bold leading-tight">Optimize App</h1>
              <p className="text-white/40 text-sm mt-0.5">Manage storage and free up space</p>
            </div>
          </div>
        </div>

        <div className="max-w-5xl mx-auto px-8 py-10">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 items-start">
            {/* Storage summary */}
            <div
              className="lg:col-span-2 p-8 rounded-[20px] flex flex-col items-center text-center"
              style={{ backgroundColor: COLORS.card }}
            >
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center"
                style={{ backgroundColor: `${COLORS.orange}1F` }}
              >
                <StorageIcon />
              </div>
              <p className="text-white/50 text-[13px] mt-4">Total App Storage</p>
              <div className="mt-1.5 h-[38px] flex items-center">
                {isLoadingUsage ? (
                  <div
                    className="w-[30px] h-[30px] rounded-full border-2 border-t-transparent animate-spin"
                    style={{ borderColor: COLORS.orange, borderTopColor: 'transparent' }}
                  />
                ) : quotaSupported ? (
                  <span className="text-white text-[28px] font-extrabold">{formatBytes(totalBytes)}</span>
                ) : (
                  <span className="text-white/40 text-sm">Not available in this browser</span>
                )}
              </div>
            </div>

            {/* Breakdown + action */}
            {quotaSupported ? (
              <div className="lg:col-span-3 flex flex-col gap-6">
                <div className="p-6 rounded-[20px]" style={{ backgroundColor: COLORS.card }}>
                  <p className="text-white text-[15px] font-bold mb-4">Storage Breakdown</p>
                  <div className="space-y-3.5">
                    <UsageRow
                      label="Cache"
                      sublabel="Cached files, thumbnails, previews"
                      icon={<CacheIcon />}
                      bytes={cacheBytes}
                      totalBytes={totalBytes}
                      loading={isLoadingUsage}
                    />
                    <UsageRow
                      label="App Data"
                      sublabel="Saved data & local settings"
                      icon={<AppDataIcon />}
                      bytes={appDataBytes}
                      totalBytes={totalBytes}
                      loading={isLoadingUsage}
                    />
                  </div>
                </div>

                <button
                  onClick={clearCache}
                  disabled={clearDisabled}
                  className="w-full h-[54px] rounded-2xl flex items-center justify-center gap-2.5 font-bold text-black text-base disabled:opacity-30 hover:brightness-105 transition-[filter]"
                  style={{ backgroundColor: COLORS.orange }}
                >
                  <ClearButtonContent />
                </button>
              </div>
            ) : (
              <div
                className="lg:col-span-3 h-full min-h-[160px] flex items-center justify-center p-8 rounded-[20px]"
                style={{ backgroundColor: COLORS.card }}
              >
                <p className="text-white/40 text-sm text-center">Storage breakdown isn't available in this browser.</p>
              </div>
            )}
          </div>

          <p className="mt-6 max-w-2xl mx-auto text-center text-white/20 text-xs leading-relaxed">
            Clearing cache removes cached files like image previews. Your account,
            messages, and saved data stay untouched.
          </p>
        </div>
      </div>

      {/* ── Toast (shared) ──────────────────────────────────────────────── */}
      {toast && (
        <div
          className="fixed left-1/2 -translate-x-1/2 bottom-8 z-50 px-4 py-2.5 rounded-xl text-sm font-medium text-black shadow-lg"
          style={{ backgroundColor: toast.isError ? '#EF4444' : COLORS.orange }}
        >
          {toast.message}
        </div>
      )}
    </div>
  );
};

export default OptimizeAppScreen;